import axios from 'axios'

export const createParamsString = (data) => {
  const params = new URLSearchParams()
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    const serialized = typeof value === 'object' ? JSON.stringify(value) : value
    params.append(key, serialized)
  })
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const setSnackbarHelper = async ({ commit }, data) => {
  commit('setSnackbar', data)
}

const sessionExpiredMessage = 'Your session has expired. Please log in again'
const sessionEndedErrorMarkers = [
  'jwt expired',
  'token is expired',
  'token expired',
  'jwt audience invalid',
  'invalid signature',
  'token is missing'
]

const isSessionExpiredMessage = (message) => {
  if (!message || typeof message !== 'string') return false
  const normalizedMessage = message.toLowerCase()
  return sessionEndedErrorMarkers.some((marker) => normalizedMessage.includes(marker))
}

const getRawErrorMessage = (error) => error?.response?.data?.message || error?.response?.data?.error || error?.error || error?.message

export const getErrorMessage = (error) => {
  const errorMessage = getRawErrorMessage(error)
  return isSessionExpiredMessage(errorMessage) ? sessionExpiredMessage : errorMessage
}

const normalizeErrorMessage = (error) => {
  if (!isSessionExpiredMessage(getRawErrorMessage(error))) return
  if (error?.response?.data?.message) error.response.data.message = sessionExpiredMessage
  if (error?.response?.data?.error) error.response.data.error = sessionExpiredMessage
  if (error?.error) error.error = sessionExpiredMessage
  if (error?.message) error.message = sessionExpiredMessage
}

export const makeAxiosRequestHelper = async ({ getters, commit, urlSlug, method, data, displaySnackbar = true }) => {
  try {
    const { data: response } = await axios({
      method,
      url: `${getters.apiUrl}/${urlSlug}`,
      data,
      ...getters.apiHeader
    })

    return response
  } catch (error) {
    const errorMessage = getErrorMessage(error)
    normalizeErrorMessage(error)
    if (displaySnackbar) setSnackbarHelper({ commit }, { open: true, text: errorMessage, type: 'error' })
    throw error
  }
}

export default {
  // Article functions
  articleGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')

    const filters = data.filters || []

    // If articleId provided, add it as a filter for single article fetch
    if (data.articleId) {
      filters.push({ field: 'articleId', op: 'eq', value: data.articleId })
    }

    const params = {
      filters,
      or: data.or,
      sort: data.sort,
      pagination: data.pagination,
      includeKeywords: data.includeKeywords,
      includeText: data.includeText ?? Boolean(data.articleId),
      includeExtendedMetrics: data.includeExtendedMetrics,
      key: data.key || data.sharingKey
    }

    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })

    if (data.articleId && !data.skipCommit) {
      const article = response?.items?.[0] || response
      await commit('loadArticle', article)
      return article
    }

    if (data.straightReturn) return response
    if (data.commitType === 'basic') {
      await commit('setArticles', response?.items || response)
    } else {
      await commit('loadArticlesAdvanced', response)
    }
    return response
  },

  articleGetListByArticle: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article/${data.articleId}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  articlePost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article`,
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  articlePut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article`,
      method: 'put',
      data,
      displaySnackbar: true
    })
  },

  // Article Translations functions
  articleTranslationsGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const params = {
      language: data.language,
      status: data.status,
      limit: data.limit,
      offset: data.offset
    }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article-translations${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    if (data.straightReturn) return response
    await commit('setTranslatedArticles', response)
  },

  articleTranslationsPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article-translations`,
      method: 'put',
      data,
      displaySnackbar: false
    })
  },

  // Article Versions functions
  articleVersionsGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const params = {
      articleId: data.articleId,
      versionId: data.versionId
    }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article-versions${createParamsString(params)}`,
      method: 'get'
    })
    if (data?.versionId) return response
    return response.versions
  },

  // Article comment functions
  articleCommentsGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!data.articleId) throw new Error('articleId is required')
    const params = {
      articleId: data.articleId,
      field: data.field,
      key: data.sharingKey || data.key
    }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article-comments${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    return response?.threads || []
  },

  articleCommentsPost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!data.articleId) throw new Error('articleId is required')
    return await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article-comments`,
      method: 'post',
      data: {
        articleId: data.articleId,
        contentplanId: data.contentplanId,
        parentId: data.parentId,
        field: data.field,
        quote: data.quote,
        prefix: data.prefix,
        suffix: data.suffix,
        startOffset: data.startOffset,
        body: data.body,
        authorName: data.authorName,
        authorEmail: data.authorEmail,
        key: data.sharingKey || data.key
      }
    })
  },

  articleCommentsPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!data.commentId) throw new Error('commentId is required')
    return await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article-comments`,
      method: 'put',
      data: {
        articleId: data.articleId,
        contentplanId: data.contentplanId,
        commentId: data.commentId,
        action: data.action,
        body: data.body,
        key: data.sharingKey || data.key
      }
    })
  },

  // Contentplan functions
  contentplanGetList: async ({ getters, commit }, data) => {
    const filters = [
      { field: 'workspaceId', op: 'eq', value: data.workspaceId }
    ]
    if (data.type) {
      filters.push({ field: 'type', op: 'eq', value: data.type })
    }
    const params = {
      filters,
      or: data.or,
      sort: data.sort,
      pagination: data.pagination
    }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('updateContentplans', response)
  },

  contentplanGetOne: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('updateActiveContentplan', response)
    return response
  },

  contentplanPost: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'contentplan',
      method: 'post',
      data
    })
    return response
  },

  contentplanPut: async ({ getters, commit }, data) => {
    const workspaceId = data.workspaceId
    if (!workspaceId) throw new Error('workspaceId is required')

    return makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'contentplan',
      method: 'put',
      data
    })
  },

  // Images functions
  imagesGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/images`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  imagesPost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/images`,
      method: 'post',
      data: {
        fileName: data.fileName,
        fileContent: data.fileContent,
        contentType: data.contentType
      },
      displaySnackbar: false
    })
    setSnackbarHelper({ commit }, { open: true, text: 'Image uploaded successfully', type: 'success' })
    return response
  },

  imagesPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/images`,
      method: 'put',
      data,
      displaySnackbar: false
    })
    const successMessage = data.action === 'delete' ? 'Image deleted successfully' : 'Image renamed successfully'
    setSnackbarHelper({ commit }, { open: true, text: successMessage, type: 'success' })
    return response
  },

  // Internal Links functions
  internalLinksGetListByContentplan: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const params = {
      articleId: data.articleId,
      urlFilter: data.urlFilter
    }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/internal-links${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('updateInternalLinks', response)
  },

  // Keyword functions
  keywordGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')

    const filters = data.filters || []

    // If keywordId provided, add it as a filter for single keyword fetch
    if (data.keywordId) {
      filters.push({ field: 'keywordId', op: 'eq', value: data.keywordId })
    }

    // If keyword text provided, add it as a filter
    if (data.keyword) {
      filters.push({ field: 'keyword', op: 'eq', value: data.keyword })
    }

    if (data.articleId !== undefined && data.articleId !== null) filters.push({ field: 'articleId', op: 'eq', value: data.articleId })

    if (data.q && data.q !== '.*') {
      filters.push({ field: 'keyword', op: 'regexp', value: data.q })
    }
    if (Array.isArray(data.source) && data.source.length > 0) {
      filters.push({ field: 'source', op: 'in', value: data.source })
    }

    const statusValues = Array.isArray(data.status) ? data.status : []
    if (statusValues.length === 1) {
      const status = statusValues[0]
      if (status === 'in_plan') {
        filters.push({ field: 'articleId', op: 'is_null', value: false })
        filters.push({ field: 'excluded', op: 'eq', value: 0 })
      } else if (status === 'not_in_plan') {
        filters.push({ field: 'articleId', op: 'is_null', value: true })
        filters.push({ field: 'excluded', op: 'eq', value: 0 })
      } else if (status === 'excluded') {
        filters.push({ field: 'excluded', op: 'eq', value: 1 })
      }
    }

    const sortFieldMap = {
      keyword: 'keyword',
      estimated_search_volume: 'estimatedSearchVolume',
      google_impressions_last: 'googleImpressionsLast',
      google_clicks_last: 'googleClicksLast',
      google_ctr_last: 'googleCtrLast',
      google_position_last: 'googlePositionLast'
    }
    const sortField = sortFieldMap[data.sortColumn] || 'estimatedSearchVolume'
    const sortDirection = data.sortDirection || 'desc'

    const limit = data.limit || 1000
    const page = data.page || 1
    const pagination = {
      limit,
      offset: Math.max(page - 1, 0) * limit
    }

    const params = {
      filters,
      sort: [{ field: sortField, direction: sortDirection }],
      pagination,
      or: data.or
    }

    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/keyword${createParamsString(params)}`,
      method: 'get',
      mutationName: 'updateContentplanKeywords'
    })

    // If fetching a single keyword, return directly
    if (data.keywordId || data.keyword) {
      return response
    }

    if (data.straightReturn) return response
    if (!data.skipCommit) await commit('updateContentplanKeywords', response)
    return response
  },

  keywordGetListByPage: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/keyword/page?pageId=${data.pageId || ''}&key=${data.sharingKey || ''}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('updatePageKeywords', response?.items || response)
  },

  keywordPost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/keyword`,
      method: 'post',
      data,
      displaySnackbar: false
    })
  },

  keywordPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/keyword`,
      method: 'put',
      data,
      displaySnackbar: false
    })
    return response
  },

  competitorGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')

    const params = {
      intent: data.intent
    }

    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/competitors${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })

    return response
  },

  // Page functions
  pageGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')

    const filters = data.filters || []

    // If pageId provided, add it as a filter for single page fetch
    if (data.pageId) {
      filters.push({ field: 'pageId', op: 'eq', value: data.pageId })
    }

    // If articleId provided, add it as a filter
    if (data.articleId) {
      filters.push({ field: 'articleId', op: 'eq', value: data.articleId })
    }

    if (data.search && data.search !== '.*') {
      filters.push({ field: 'url', op: 'regexp', value: data.search })
    }
    if (data.excluded === '0' || data.excluded === '1') {
      filters.push({ field: 'excluded', op: 'eq', value: Number(data.excluded) })
    }
    if (data.typeFilter && data.typeFilter !== 'null') {
      const types = Array.isArray(data.typeFilter)
        ? data.typeFilter
        : String(data.typeFilter).split(',').map(t => t.trim()).filter(Boolean)
      if (types.length) {
        filters.push({ field: 'type', op: 'in', value: types })
      }
    }

    const sortFieldMap = {
      url: 'url',
      google_impressions_last: 'googleImpressionsLast',
      google_impressions_change: 'googleImpressionsChange',
      google_clicks_last: 'googleClicksLast',
      google_position_last: 'googlePositionLast',
      status_code: 'statusCode',
      published_at: 'publishedAt',
      type: 'type'
    }
    const sortField = sortFieldMap[data.sortColumn] || 'googleImpressionsLast'
    const sortDirection = data.sortDirection || 'desc'

    const limit = data.limit || 1000
    const page = data.page || 1
    const pagination = {
      limit,
      offset: Math.max(page - 1, 0) * limit
    }

    const params = {
      filters,
      sort: [{ field: sortField, direction: sortDirection }],
      pagination,
      groups: data.groups,
      or: data.or,
      key: data.sharingKey || data.key
    }

    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })

    // If fetching a single page, return directly
    if (data.pageId) {
      return response
    }

    if (!data.skipCommit) commit('updatePages', response)
    return response
  },

  pageGetProductsByWordpressIds: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!Array.isArray(data.ids)) throw new Error('ids must be an array')
    if (!data.ids.length) return { items: [] }

    const ids = [...new Set(data.ids.map(id => String(id).trim()).filter(Boolean))]
    if (!ids.length) return { items: [] }

    const params = {
      ids: ids.join(','),
      articleId: data.articleId || null,
      key: data.sharingKey || data.key || null
    }

    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page/products-shortcode${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  pageGetListByKeywords: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `page/keywords${createParamsString(data)}`,
      method: 'get',
      displaySnackbar: false
    })
    commit('updatePages', response)
  },

  pagePost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page`,
      method: 'post',
      data
    })
    return response
  },

  pagePut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page`,
      method: 'put',
      data
    })
  },

  categoryContentPost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/ecom/suggest-new`,
      method: 'post',
      data
    })
    return response
  },

  pageTranslationsGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const params = {
      pageId: data.pageId,
      language: data.language,
      type: data.type,
      status: data.status,
      limit: data.limit,
      offset: data.offset
    }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page-translations${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  pageTranslationsPost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!data.type) throw new Error('type is required')
    if (!Array.isArray(data.pageIds) || !data.pageIds.length) throw new Error('pageIds are required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page-translations`,
      method: 'post',
      data
    })
    return response
  },

  pageTranslationsPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!data.pageId) throw new Error('pageId is required')
    if (!data.language) throw new Error('language is required')
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page-translations`,
      method: 'put',
      data,
      displaySnackbar: false
    })
  },

  categoryContentSettingsGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}`,
      method: 'get',
      displaySnackbar: false
    })
    return { prompt: response?.category_content_prompt || '' }
  },

  categoryContentSettingsPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'contentplan',
      method: 'put',
      data: {
        contentplanId: data.contentplanId,
        action: 'update',
        category_content_prompt: data.prompt || ''
      },
      displaySnackbar: false
    })
    return { prompt: data.prompt || '' }
  },

  uploadWordpressCategory: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!data.categoryId) throw new Error('categoryId is required')
    if (!data.metaTitle && !data.description) throw new Error('metaTitle or description is required')
    if (data.language && (!data.metaTitle || !data.description)) throw new Error('metaTitle and description are required for translated category uploads')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/wordpress/category',
      method: 'post',
      data
    })
    return response
  },

  getWordpressCategories: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `integrations/wordpress/categories${createParamsString(data)}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  // Page Similarity functions
  pageSimilarityTriggerAnalysis: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page-similarity/analyze`,
      method: 'post',
      data: {}
    })
    return response
  },

  pageSimilarityGetClusters: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/page-similarity`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  // Sprints functions
  sprintsGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/sprints`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  sprintsPost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/sprints`,
      method: 'post',
      data
    })
    return response
  },

  sprintsPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/sprints/${data.sprintId}`,
      method: 'put',
      data
    })
    return response
  },

  // Topic functions
  topicGetList: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const params = {
      filters: data.filters,
      or: data.or,
      sort: data.sort,
      pagination: data.pagination
    }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/topic${createParamsString(params)}`,
      method: 'get'
    })
    await commit('loadTopics', response)
  },

  topicPost: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/topic`,
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  topicPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/topic`,
      method: 'put',
      data,
      displaySnackbar: false
    })
  },

  mindmapShareGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    if (!data.key) throw new Error('key is required')
    const params = { key: data.key }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/mindmap-share${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  // Legacy and non-core functions (kept for compatibility)
  adminContentplansOverviewGet: async ({ getters, commit }) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'admin/contentplans-overview',
      method: 'get'
    })
    return response
  },

  adminContentplansOverviewPost: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'admin/contentplans-overview',
      method: 'post',
      data
    })
    return response
  },

  adminContentplansOverviewPut: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `admin/contentplans-overview/${data.contentplanId}`,
      method: 'put',
      data
    })
    return response
  },

  adminLinkSourcesGet: async ({ getters, commit }, data) => {
    if (!data.contentplanId) throw new Error('contentplanId is required')
    const params = { contentplanId: data.contentplanId, limit: data.limit, minScore: data.minScore }
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `admin/link-sources${createParamsString(params)}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  adminImpactReportGet: async ({ getters, commit }, data = {}) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `admin/impact-report${createParamsString({ weeks: data.weeks })}`,
      method: 'get'
    })
    return response
  },

  adminInvestorReportGet: async ({ getters, commit }, data = {}) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `admin/investor-report${createParamsString({ hash: data.hash })}`,
      method: 'get'
    })
    return response
  },

  sendEmail: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'admin/send-email',
      method: 'post',
      data
    })
    return response
  },

  getGSCReportBundle: async ({ getters, commit, state }, data) => {
    const {
      commitToStore = true,
      appendQuery = false,
      appendPage = false,
      ...requestData
    } = data

    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${requestData.contentplanId}/gsc`,
      method: 'post',
      data: {
        ...requestData,
        reportBundle: true
      },
      displaySnackbar: false
    })

    const mergeRowsByKey = (existingRows = [], incomingRows = []) => {
      const merged = [...existingRows]
      const existingKeys = new Set(existingRows.map(row => JSON.stringify(row.keys || [])))

      incomingRows.forEach(row => {
        const rowKey = JSON.stringify(row.keys || [])
        if (existingKeys.has(rowKey)) return
        existingKeys.add(rowKey)
        merged.push(row)
      })

      return merged.sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
    }

    const requestedSections = Array.isArray(requestData.sections) ? requestData.sections : ['DATE', 'QUERY', 'PAGE']
    const hasDateSection = requestedSections.includes('DATE')
    const hasQuerySection = requestedSections.includes('QUERY')
    const hasPageSection = requestedSections.includes('PAGE')
    const hasPageTypeSection = requestedSections.includes('PAGE_TYPE')

    if (!commitToStore) return response

    if (hasDateSection) commit('updateGSCDataByDate', Array.isArray(response?.byDate) ? response.byDate : [])

    if (hasQuerySection) {
      const incomingQueryRows = Array.isArray(response?.byQuery) ? response.byQuery : []
      const nextQueryRows = appendQuery ? mergeRowsByKey(state.gsc.byQuery, incomingQueryRows) : incomingQueryRows
      commit('updateGSCDataByQuery', nextQueryRows)
    }

    if (hasPageSection) {
      const incomingPageRows = Array.isArray(response?.byPage) ? response.byPage : []
      const nextPageRows = appendPage ? mergeRowsByKey(state.gsc.byPage, incomingPageRows) : incomingPageRows
      commit('updateGSCDataByPage', nextPageRows)
    }

    if (hasPageTypeSection) {
      commit('updateGSCDataByPageTypeDate', Array.isArray(response?.byPageTypeDate) ? response.byPageTypeDate : [])
      commit('updateGSCDataByPageTypeSummary', Array.isArray(response?.byPageTypeSummary) ? response.byPageTypeSummary : [])
    }

    const nextPagination = {}
    if (hasQuerySection) nextPagination.queryNextCursor = response?.pagination?.queryNextCursor ?? null
    if (hasPageSection) nextPagination.pageNextCursor = response?.pagination?.pageNextCursor ?? null
    if (Object.keys(nextPagination).length) commit('updateGSCPagination', nextPagination)
    commit('updateGSCMeta', response?.meta || {})

    return response
  },

  getAboutInfoAuto: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/about-info`,
      method: 'post',
      data
    })
    return response
  },

  postHierarchicalClustering: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'hierarchical-clustering',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  loadWorkspaces: async ({ getters, commit }) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'workspace',
      method: 'get',
      displaySnackbar: false
    })
    await commit('updateWorkspaces', response)
    if (getters.activeWorkspace?.id) {
      const activeWorkspace = response.find(workspace => workspace.id === getters.activeWorkspace.id)
      if (activeWorkspace) await commit('updateActiveWorkspace', activeWorkspace)
    }
    return response
  },

  updateWorkspace: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `workspace/${data.workspaceId}`,
      method: 'put',
      data,
      displaySnackbar: false
    })
    return response
  },

  runClustererHttp: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'clusterer',
      method: 'post',
      data
    })
    return response
  },

  getRelatedKeywords: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/keyword/related`,
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  getRelatedKeywordsNlp: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/keyword/related`,
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  processKeywords: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'keyword/process',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  postHubExpand: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/cluster/new-ideas`,
      method: 'post',
      data
    })
    return response
  },

  loadArticleUrls: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `article/${data.articleId}/url?key=${data.sharingKey || ''}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('updateArticleUrls', response)
  },

  loadArticleExternalUrls: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `article/${data.articleId}/url/external?key=${data.sharingKey || ''}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('updateExternalUrls', response)
  },

  uploadArticle: async ({ getters, commit }, data) => {
    const platformEndpoints = {
      wordpress: 'integrations/wordpress/page',
      autoblog: 'integrations/autoblog/page',
      webflow: 'integrations/site-builder/page',
      shopify: 'integrations/shopify/page',
      'custom-cms': 'integrations/custom-cms/page',
      'gecko-cms': `cms/contentplans/${data.contentplanId}/page`,
      magento: 'integrations/magento/page'
    }

    const endpoint = platformEndpoints[data.publishTo]
    if (!endpoint) {
      throw new Error(`Unsupported publish platform: ${data.publishTo}`)
    }

    return makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: endpoint,
      method: 'post',
      data,
      displaySnackbar: false
    })
  },

  uploadTranslatedArticle: async ({ getters, commit }, data) => {
    const publishTo = data.publishTo || getters.activeContentplan?.main_cms_integration || 'wordpress'
    const platformEndpoints = {
      wordpress: 'integrations/wordpress/page-translated',
      webflow: 'integrations/site-builder/page-translated',
      magento: 'integrations/magento/page-translated',
      shopify: 'integrations/shopify/page-translated',
      'custom-cms': 'integrations/custom-cms/page-translated',
      'gecko-cms': `cms/contentplans/${data.contentplanId}/page-translated`
    }
    const endpoint = platformEndpoints[publishTo]
    if (!endpoint) throw new Error(`Unsupported translated publish platform: ${publishTo}`)

    return makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: endpoint,
      method: 'post',
      data,
      displaySnackbar: false
    })
  },

  getCmsSiteByContentplan: async ({ getters, commit }, data) => {
    return makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `cms/contentplans/${data.contentplanId}/site`,
      method: 'get',
      displaySnackbar: false
    })
  },

  postCmsSite: async ({ getters, commit }, data) => {
    return makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'cms/sites',
      method: 'post',
      data,
      displaySnackbar: false
    })
  },

  putCmsSite: async ({ getters, commit }, data) => {
    return makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `cms/sites/${data.siteId}`,
      method: 'put',
      data,
      displaySnackbar: false
    })
  },

  postCmsSiteReadKey: async ({ getters, commit }, data) => {
    return makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `cms/sites/${data.siteId}/keys`,
      method: 'post',
      data,
      displaySnackbar: false
    })
  },

  recalculateAlerts: async ({ getters, commit }, data) => {
    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/alert`,
      method: 'post',
      displaySnackbar: false
    })
  },

  loadScoreAndCommit: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article/score?articleId=${data.articleId || ''}&keywordId=${data.keywordId || ''}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('updateScore', response)
  },

  loadScoreAndReturn: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/article/score?articleId=${data.articleId || ''}&keywordId=${data.keywordId || ''}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  postUser: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'user',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  getIntegrations: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `integrations/${data.contentplanId}`,
      method: 'get',
      displaySnackbar: false
    })
    await commit('integrations', response)
    return response
  },

  deleteIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/delete',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  postWordpressIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/wordpress',
      method: 'post',
      data,
      displaySnackbar: false
    })
    await commit('integrations', response)
    return response
  },

  postNotionIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/notion',
      method: 'post',
      data,
      displaySnackbar: false
    })
    await commit('integrations', response)
    return response
  },

  postWebflowIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/site-builder',
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  postShopifyIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/shopify',
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  getGoogleAnalyticsProperties: async ({ getters, commit }) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/google-analytics-properties',
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  saveGoogleAnalyticsProperty: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/google-analytics-property',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  saveBigQueryConfig: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/bigquery-config',
      method: 'post',
      data
    })
    return response
  },

  getBigQueryContentAttribution: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/bigquery-attribution',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  getGoogleAnalyticsData: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/google-analytics-data',
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  getSearchConsoleProperties: async ({ getters, commit }) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/search-console/properties',
      method: 'get',
      displaySnackbar: true
    })
    return response
  },

  postSearchConsoleIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/search-console',
      method: 'post',
      data
    })
    return response
  },

  postAutoblogIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/autoblog',
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  getStripeProducts: async ({ getters, commit }) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/stripe/products',
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  createStripeCheckoutSession: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/stripe/checkout-session',
      method: 'post',
      data
    })
    return response
  },

  getStripeLink: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `integrations/stripe/portal-link${createParamsString(data)}`,
      method: 'get'
    })
    return response
  },

  initWebsocket: async ({ getters, commit, dispatch, state }) => {
    if (state.websocketInitializing) {
      console.log('WebSocket initialization already in progress, skipping...')
      return
    }

    if (getters.websocketInstance) {
      console.log('Closing existing WebSocket connection')
      getters.websocketInstance.close()
    }

    commit('setWebsocketInitializing', true)

    try {
      let websocketUrl = getters.websocketUrl
      if (getters.token) {
        const separator = websocketUrl.includes('?') ? '&' : '?'
        websocketUrl = `${websocketUrl}${separator}token=${getters.token}`
      }
      const socket = new WebSocket(websocketUrl)
      commit('setWebsocket', socket)

      socket.onopen = () => {
        console.log('WebSocket connected')
        commit('setWebsocketConnected', true)
        commit('resetReconnectAttempts')
        commit('setWebsocketInitializing', false)
      }

      socket.onmessage = (event) => {
        const eventData = JSON.parse(event.data)
        console.log('Received websocket message', eventData)
        commit('addWebsocketMessage', eventData)

        dispatch('handleWebsocketMessage', eventData)
      }

      socket.onerror = (error) => {
        console.error('WebSocket error: ', error)
        commit('setWebsocketInitializing', false)
      }

      socket.onclose = (event) => {
        console.log('WebSocket closed with code:', event.code)
        commit('setWebsocketConnected', false)
        commit('setWebsocketInitializing', false)

        if (event.code === 1006 && getters.websocketReconnectAttempts < getters.websocketMaxReconnectAttempts) {
          commit('incrementReconnectAttempts')
          const delay = Math.min(1000 * Math.pow(2, getters.websocketReconnectAttempts), 10000)

          setTimeout(() => {
            dispatch('initWebsocket')
          }, delay)
        }
      }
    } catch (error) {
      console.error('Error initializing WebSocket:', error)
      commit('setWebsocketInitializing', false)
    }
  },

  sendWebsocketMessage: async ({ getters, dispatch }, message) => {
    try {
      if (!getters.websocketConnected) {
        await dispatch('initWebsocket')

        await new Promise((resolve) => {
          const checkConnection = () => {
            if (getters.websocketConnected) {
              resolve()
            } else {
              setTimeout(checkConnection, 100)
            }
          }
          checkConnection()
        })
      }

      getters.websocketInstance.send(JSON.stringify(message))
    } catch (err) {
      console.error('Error sending websocket message', err)
    }
  },

  handleWebsocketMessage: (_, message) => {
    console.log('Processing websocket message', message)
  },

  closeWebsocket: ({ getters, commit }) => {
    if (getters.websocketInstance) {
      getters.websocketInstance.close()
      commit('setWebsocket', null)
      commit('setWebsocketConnected', false)
    }
  },

  getWorkspaceUsers: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `workspace/user?workspaceId=${data.workspaceId}`,
      method: 'get'
    })
    commit('updateWorkspaceUsers', response)
    return response
  },

  setSnackbar: setSnackbarHelper,

  getAutoblogSettings: async ({ commit, getters }, { contentplanId }) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${contentplanId}/autoblog-settings`,
      method: 'get'
    })
    commit('setAutoblogSettings', response)
    return response
  },

  updateAutoblogSettings: async ({ commit, getters }, { contentplanId, settings }) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${contentplanId}/autoblog-settings`,
      method: 'put',
      data: settings
    })
    commit('setAutoblogSettings', response)
  },

  getWebflowSites: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/site-builder/sites',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  getWebflowCollections: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/site-builder/collections',
      method: 'post',
      data,
      displaySnackbar: false
    })
    return response
  },

  getWebflowCollectionSchema: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/site-builder/collection-schema',
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  initiateShopifyAuth: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/shopify/oauth',
      method: 'post',
      data: {
        ...data,
        action: 'initiate'
      }
    })
    return response
  },

  completeShopifyAuth: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/shopify/oauth',
      method: 'post',
      data: {
        ...data,
        action: 'callback'
      }
    })
    return response
  },

  resolveShopifyStoreDomain: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/shopify/oauth',
      method: 'post',
      data: {
        ...data,
        action: 'resolve-store-domain'
      },
      displaySnackbar: false
    })
    return response
  },

  saveShopifyIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/shopify',
      method: 'post',
      data
    })
    return response
  },

  getShopifyBlogs: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `integrations/shopify/blogs${createParamsString(data)}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  postCustomCmsIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/custom-cms',
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  postMagentoIntegration: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'integrations/magento',
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  async uploadDocument ({ getters, commit }, data) {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'documents',
      method: 'post',
      data: {
        contentplanId: data.contentplanId,
        fileName: data.fileName,
        contentType: data.contentType,
        fileSize: data.fileSize
      },
      displaySnackbar: false
    })

    await axios.put(response.uploadUrl, data.file, {
      headers: {
        'Content-Type': data.contentType,
        'x-amz-server-side-encryption': 'AES256'
      }
    })

    await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'documents',
      method: 'put',
      data: {
        contentplanId: data.contentplanId,
        documentId: response.documentId
      },
      displaySnackbar: false
    })

    setSnackbarHelper({ commit }, { open: true, text: 'Document uploaded successfully', type: 'success' })
    return response
  },

  async getDocuments ({ getters, commit }, { contentplanId }) {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `documents?contentplanId=${contentplanId}`,
      method: 'get',
      displaySnackbar: false
    })
    return response
  },

  async uploadCopilotAttachment ({ getters, commit }, file) {
    const contentType = file.type || 'application/octet-stream'
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'copilot/upload-url',
      method: 'post',
      data: {
        contentType,
        fileName: file.name,
        fileSize: file.size
      },
      displaySnackbar: false
    })

    await axios.put(response.uploadUrl, file, {
      headers: {
        'Content-Type': contentType,
        'x-amz-server-side-encryption': 'AES256'
      }
    })

    return {
      contentType: response.contentType,
      fileName: response.fileName,
      key: response.key,
      sizeBytes: response.sizeBytes
    }
  },

  async deleteDocument ({ getters, commit }, { contentplanId, documentId }) {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: 'documents',
      method: 'delete',
      data: { contentplanId, documentId }
    })
    return response
  },

  getTopicSuggestions: async ({ getters, commit }, data) => {
    const response = await makeAxiosRequestHelper({
      getters,
      commit,
      urlSlug: `contentplan/${data.contentplanId}/topic/new-ideas`,
      method: 'post',
      data,
      displaySnackbar: true
    })
    return response
  },

  performMemoryCleanup: ({ commit }) => {
    commit('clearLargeStateObjects')
    commit('cleanupProgressTrackers')
    commit('clearWebsocketMessages')
  }
}
